import { Injectable, NgZone } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Platform } from '@ionic/angular';
import { InAppPurchase2, IAPProduct } from '@awesome-cordova-plugins/in-app-purchase-2/ngx';
import { Subject } from 'rxjs';
import { RequestService } from '../request/request.service';
import { ProfileService } from './profile.service';

@Injectable({ providedIn: 'root' })
export class SubscriptionReconciliationService {
  readonly updates = new Subject<any>();
  purchaseInProgress = false;
  private inFlight?: Promise<any>;
  private timer?: ReturnType<typeof setTimeout>;
  private listener?: { remove: () => Promise<void> };
  private monitoring = false;
  private foreground = true;
  private generation = 0;
  private retryCycle = 0;
  private attemptsThisSession = 0;
  // Initial check plus two retries: three attempts per Home/resume cycle.
  private readonly retryDelays = [5000, 15000];
  private readonly productId = '1618';

  constructor(
    private request: RequestService,
    private profiles: ProfileService,
    private store: InAppPurchase2,
    private platform: Platform,
    private zone: NgZone,
  ) {}

  start(): void {
    this.foreground = true;
    if (this.monitoring) {
      console.info('[Subscription reconciliation] Already monitoring');
      return;
    }
    console.info('[Subscription reconciliation] Started: Home entered');
    this.monitoring = true;
    const generation = ++this.generation;
    void App.addListener('appStateChange', ({ isActive }) => {
      console.info('[Subscription reconciliation] App state changed', { active: isActive });
      if (this.foreground === isActive) return;
      this.foreground = isActive;
      clearTimeout(this.timer);
      this.retryCycle++;
      if (isActive) {
        this.attemptsThisSession = 0;
        this.beginCheck();
      }
    }).then(listener => {
      if (!this.monitoring || generation !== this.generation) void listener.remove();
      else this.listener = listener;
    }).catch(() => undefined);
    this.beginCheck();
  }

  stop(): void {
    console.info('[Subscription reconciliation] Stopped: Home left');
    this.monitoring = false;
    this.generation++;
    this.retryCycle++;
    clearTimeout(this.timer);
    void this.listener?.remove();
    this.listener = undefined;
  }

  private beginCheck(): void {
    if (this.attemptsThisSession >= this.retryDelays.length + 1) {
      console.info('[Subscription reconciliation] Session retry limit reached');
      return;
    }
    clearTimeout(this.timer);
    console.info('[Subscription reconciliation] New check cycle');
    void this.tick(++this.retryCycle, 0);
  }

  private async tick(cycle: number, attempt: number): Promise<void> {
    if (!this.monitoring || !this.foreground || cycle !== this.retryCycle) return;
    if (this.attemptsThisSession >= this.retryDelays.length + 1) return;
    this.attemptsThisSession++;
    console.info('[Subscription reconciliation] Attempt', {
      attempt: attempt + 1, maximum: this.retryDelays.length + 1,
    });
    const result = await this.sync();
    // Success ends the job. A new entry/resume starts a fresh check; there
    // is no recurring timer after successful synchronization.
    if (result) {
      console.info('[Subscription reconciliation] Completed', { state: result.subscription_state });
      return;
    }
    if (!this.monitoring || !this.foreground || cycle !== this.retryCycle) return;
    if (attempt < this.retryDelays.length) {
      console.info('[Subscription reconciliation] Retry scheduled', {
        nextAttempt: attempt + 2, delayMs: this.retryDelays[attempt],
      });
      this.timer = setTimeout(() => void this.tick(cycle, attempt + 1), this.retryDelays[attempt]);
    } else {
      console.info('[Subscription reconciliation] Retry limit reached for this cycle');
    }
  }

  sync(): Promise<any> {
    if (this.purchaseInProgress) return Promise.resolve(null);
    if (this.inFlight) return this.inFlight;
    const promise = this.reconcile().catch(error => {
      console.warn('[Subscription reconciliation] Check failed', {
        message: error instanceof Error ? error.message : String(error),
      });
      return null;
    });
    this.inFlight = promise;
    void promise.then(result => {
      if (this.inFlight === promise) this.inFlight = undefined;
      // A successful check initiated by Pago Verificación also cancels a
      // pending Home retry, since both callers share the same job.
      if (result) clearTimeout(this.timer);
    });
    return promise;
  }

  private async currentProfile(): Promise<any> {
    const raw = await this.request.createRequestGet('profile', { verification_refresh: Date.now() });
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return data?.profile || data?.data?.profile || data;
  }

  private async reconcile(): Promise<any> {
    const generation = this.generation;
    await this.platform.ready();
    const profile = await this.currentProfile();
    if (!profile?.id || profile.owner !== true || this.purchaseInProgress) return null;
    let pendingApple = false;
    try { pendingApple = localStorage.getItem(`pending-apple-subscription:${profile.id}`) === '1'; } catch {}
    let result: any;
    let product: IAPProduct | undefined;
    if (Capacitor.getPlatform() === 'ios' && (profile.subscription_payment_source !== 'web_openpay' || pendingApple)) {
      if (!this.store.get(this.productId)) {
        this.store.register({ id: this.productId, alias: this.productId, type: this.store.PAID_SUBSCRIPTION });
      }
      await this.refreshStore();
      if (this.purchaseInProgress || generation !== this.generation) return null;
      product = this.store.get(this.productId);
      if (!product) return null;
      const expiry = product.expiryDate ? new Date(product.expiryDate).getTime() : NaN;
      const expired = Number.isFinite(expiry) && expiry <= Date.now();
      const owned = product.owned || product.state === this.store.OWNED || product.state === this.store.APPROVED;
      // Absence of ownership alone can also mean an unavailable receipt.
      if (!expired && (!owned || (product.expiryDate && !Number.isFinite(expiry)))) return null;
      // Check the current login again after the asynchronous StoreKit refresh.
      const current = await this.currentProfile();
      if (String(current?.id) !== String(profile.id) || this.purchaseInProgress) return null;
      const transaction: any = product.transaction || {};
      result = await this.request.createRequestPost('IN-APP-PURCHASE-2', {
        profile_id: profile.id,
        payment_source: 'apple',
        subscription_state: expired ? 'inactive' : 'active',
        product_id: product.id,
        ...(transaction.id ? { transaction_id: String(transaction.id) } : {}),
        ...(transaction.original_transaction_id ? { original_transaction_id: String(transaction.original_transaction_id) } : {}),
        ...(Number.isFinite(expiry) ? { expires_at: new Date(expiry).toISOString() } : {}),
      });
    } else {
      console.info('[Subscription reconciliation] getOpenPaySubscriptionByUser request', {
        reason: 'Checking a web OpenPay subscription',
      });
      result = await this.request.createRequestPost('getOpenPaySubscriptionByUser', {});
    }
    result = typeof result === 'string' ? JSON.parse(result) : result;
    if (!product) {
      console.info('[Subscription reconciliation] OpenPay response', {
        state: result?.subscription_state || 'missing',
        ignoredSourceMismatch: result?.ignored_source_mismatch === true,
      });
    }
    if (result?.ignored_source_mismatch || !['active', 'inactive'].includes(result?.subscription_state)) return null;
    if (generation !== this.generation || this.purchaseInProgress) return null;
    const fresh = await this.currentProfile();
    if (String(fresh?.id) !== String(profile.id)) return null;
    const status = Number(result.profile_status_id);
    const names: Record<number, string> = { 1: 'Sin Validar', 2: 'En Validacion', 3: 'Validado', 5: 'Pagado', 9: 'MailVerification', 10: 'Verified' };
    const updated = {
      ...fresh,
      ...result,
      ...(names[status] ? { kyc: names[status] } : {}),
    };
    if (product) {
      try { localStorage.removeItem(`pending-apple-subscription:${profile.id}`); } catch {}
      if (result.subscription_state === 'active' && product.state === this.store.APPROVED) product.finish();
    }
    this.zone.run(() => {
      if (String(this.profiles.profile?.id) === String(profile.id)) Object.assign(this.profiles.profile, updated);
      this.profiles.profile = updated;
      this.updates.next(updated);
    });
    return updated;
  }

  private refreshStore(): Promise<void> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (error?: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        error ? reject(error) : resolve();
      };
      const timer = setTimeout(() => finish(new Error('Subscription refresh timed out')), 15000);
      try {
        const refresh = this.store.refresh();
        refresh?.completed(() => finish());
        refresh?.failed(() => finish(new Error('Subscription refresh failed')));
        refresh?.cancelled(() => finish(new Error('Subscription refresh cancelled')));
      } catch (error) { finish(error as Error); }
    });
  }
}
